import * as React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from './components/site/Spinner';
import { useAuth } from './lib/auth';

const lazy = (factory: () => Promise<{ default: React.ComponentType }>) =>
  React.lazy(factory as unknown as () => Promise<{ default: React.ComponentType }>);

const SiteLayout = lazy(() => import('./components/site/SiteLayout'));
const Home = lazy(() => import('./pages/site/Home'));
const Status = lazy(() => import('./pages/site/Status'));
const StatusPage = lazy(() => import('./pages/site/Status'));
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const ConsoleLayout = lazy(() => import('./components/console/ConsoleLayout'));

const Overview = lazy(() => import('./pages/console/Overview'));
const Projects = lazy(() => import('./pages/console/Projects'));
const ProjectDetail = lazy(() => import('./pages/console/ProjectDetail'));
const Deployments = lazy(() => import('./pages/console/Deployments'));
const DeploymentDetail = lazy(() => import('./pages/console/DeploymentDetail'));
const Domains = lazy(() => import('./pages/console/Domains'));
const Dns = lazy(() => import('./pages/console/Dns'));
const PagesScreen = lazy(() => import('./pages/console/PagesScreen'));
const Functions = lazy(() => import('./pages/console/Functions'));
const Databases = lazy(() => import('./pages/console/Databases'));
const Storage = lazy(() => import('./pages/console/Storage'));
const ObjectBrowser = lazy(() => import('./pages/console/ObjectBrowser'));
const Kv = lazy(() => import('./pages/console/Kv'));
const Queues = lazy(() => import('./pages/console/Queues'));
const Analytics = lazy(() => import('./pages/console/Analytics'));
const Logs = lazy(() => import('./pages/console/Logs'));
const Monitoring = lazy(() => import('./pages/console/Monitoring'));
const ApiConsole = lazy(() => import('./pages/console/ApiConsole'));
const ApiKeys = lazy(() => import('./pages/console/ApiKeys'));
const Cli = lazy(() => import('./pages/console/Cli'));
const Sdks = lazy(() => import('./pages/console/Sdks'));
const Marketplace = lazy(() => import('./pages/console/Marketplace'));
const Team = lazy(() => import('./pages/console/Team'));
const Billing = lazy(() => import('./pages/console/Billing'));
const Settings = lazy(() => import('./pages/console/Settings'));

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Checking your session…" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

export default function App() {
  return (
    <React.Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <Routes>
        {/* A) Public marketing site */}
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/status" element={<Status />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        {/* B) Console (session-protected) */}
        <Route
          path="/console"
          element={
            <RequireAuth>
              <ConsoleLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Overview />} />
          <Route path="projects" element={<Projects />} />
          <Route path="projects/:projectId" element={<ProjectDetail />} />
          <Route path="deployments" element={<Deployments />} />
          <Route path="deployments/:deploymentId" element={<DeploymentDetail />} />
          <Route path="domains" element={<Domains />} />
          <Route path="dns" element={<Dns />} />
          <Route path="pages" element={<PagesScreen />} />
          <Route path="functions" element={<Functions />} />
          <Route path="databases" element={<Databases />} />
          <Route path="storage" element={<Storage />} />
          <Route path="storage/:bucketId" element={<ObjectBrowser />} />
          <Route path="kv" element={<Kv />} />
          <Route path="queues" element={<Queues />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="logs" element={<Logs />} />
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="api" element={<ApiConsole />} />
          <Route path="api-keys" element={<ApiKeys />} />
          <Route path="cli" element={<Cli />} />
          <Route path="sdks" element={<Sdks />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="team" element={<Team />} />
          <Route path="billing" element={<Billing />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}
